import { useTranslation } from "react-i18next";
import { Eye, EyeOff, LayoutDashboard } from "lucide-react";

import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ACT_LABELS,
  FINAL_ACT,
  FINAL_ACT_OPENED_SUB,
  SUMMARY_TAB,
  TAB_IDS,
  actNumber,
  type DiagnosticTab,
} from "@/components/projects/diagnostic/acts";

/**
 * A barra dos atos (#852). Percorre só os visíveis: o Ato 3 saiu do roteiro na #1476.
 * O Resumo (EF-DIAG-004) divide a barra, mas não é ato: sem número e sem `data-act`.
 *
 * Continua sendo o `Tabs` do Radix, e isso é decisão medida, não inércia.
 * Escrever a barra como `div` com `onClick`, que era o caminho óbvio, perderia
 * papel, foco, Enter/Space e tab stop único: regressão de acessibilidade contra
 * o que a tela já tinha.
 *
 * O que muda é o `activationMode="manual"`, aplicado no `Tabs` que envolve esta
 * barra. Com o default (`automatic`), o Radix ativa a aba que recebe foco, e o
 * `loop` é ligado: uma seta para a esquerda a partir do Ato 1 põe foco no Ato 5,
 * dispara `onValueChange` e **revela o número sem ninguém ter pedido**. Medido no
 * bundle do Radix: `activationMode = "automatic"` e `loop = true` são os padrões,
 * e a ativação por foco está no `onFocus` do trigger.
 *
 * O Ato 5 nunca é `disabled`. O Radix tira o desabilitado da navegação por
 * setas, e quem usa teclado perderia até a informação de que existe um quinto
 * ato. Ele é navegável e anuncia o próprio estado pelo sub-rótulo.
 */
export function ActStepper({
  act,
  revealed,
  notes,
  onToggleNotes,
}: {
  act: DiagnosticTab;
  revealed: boolean;
  notes: boolean;
  onToggleNotes: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="diagnostic-act-stepper"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#e8ebf0] bg-white px-8 py-3"
    >
      <TabsList className="inline-flex h-auto w-fit flex-wrap items-center justify-start gap-1 bg-transparent p-0">
        {TAB_IDS.map((id) => {
          if (id === SUMMARY_TAB) return <SummaryTrigger key={id} active={act === id} />;
          const n = actNumber(id);
          const active = id === act;
          const locked = id === FINAL_ACT && !revealed;
          const sub =
            id === FINAL_ACT && revealed ? FINAL_ACT_OPENED_SUB : ACT_LABELS[id].sub;

          return (
            <TabsTrigger
              key={id}
              value={id}
              data-act={n}
              /*
                O ativo se marca por **baixo**, não por caixa. Cinco botões de
                borda arredondada lado a lado competem com os cards do corpo pela
                mesma leitura, e o roteiro é navegação, não conteúdo. O
                `border-b-2` fica nos dois estados para o texto não pular dois
                pixels ao trocar de ato.
              */
              className={[
                "group flex h-auto items-center gap-2.5 rounded-none border-0 border-b-2 px-3 pb-2 pt-1.5 text-left transition-colors",
                "data-[state=active]:shadow-none",
                active
                  ? "border-b-[#08254c] bg-transparent"
                  : "border-b-transparent hover:bg-[#f7f8fa]",
              ].join(" ")}
            >
              <span
                aria-hidden
                className={[
                  "flex h-6 w-6 flex-none items-center justify-center rounded-full font-mono text-[11px] font-semibold",
                  locked ? "border border-dashed" : "border",
                  active
                    ? "border-[#ffc83d] bg-[#ffc83d] text-[#08254c]"
                    : locked
                      ? "border-[#b2bccb] text-[#b2bccb]"
                      : "border-[#d4dae3] text-[#7b8799]",
                ].join(" ")}
              >
                {n}
              </span>
              <span className="flex flex-col leading-tight">
                <span
                  className={[
                    "text-[13px] font-semibold",
                    active ? "text-[#08254c]" : "text-[#596b85]",
                  ].join(" ")}
                >
                  {t(ACT_LABELS[id].label)}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
                  {t(sub)}
                </span>
              </span>
            </TabsTrigger>
          );
        })}
      </TabsList>

      <div className="ml-auto flex items-center gap-4">
        {/*
          O desenho põe este toggle na ShellBar. Ele mora aqui porque a ShellBar
          é montada pelo AppLayout sem props e é compartilhada por todas as
          telas: levar um controle da devolutiva para lá vazaria o Diagnóstico
          para o resto do produto. A barra dos atos é a chrome de apresentação, e
          é onde o controle pertence.
        */}
        <button
          type="button"
          data-testid="diagnostic-notes-toggle"
          aria-pressed={notes}
          onClick={onToggleNotes}
          className={[
            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors",
            notes
              ? "border-[#08254c] bg-[#08254c] text-white"
              : "border-[#d4dae3] text-[#7b8799] hover:text-[#08254c]",
          ].join(" ")}
        >
          {notes ? (
            <Eye aria-hidden className="h-3.5 w-3.5" />
          ) : (
            <EyeOff aria-hidden className="h-3.5 w-3.5" />
          )}
          {t("Presenter notes")}
        </button>
      </div>
    </div>
  );
}

/** A aba do resumo: mesmo desenho dos atos, com um ícone no lugar do número. */
function SummaryTrigger({ active }: { active: boolean }) {
  const { t } = useTranslation();

  return (
    <TabsTrigger
      value={SUMMARY_TAB}
      className={[
        "group flex h-auto items-center gap-2.5 rounded-none border-0 border-b-2 px-3 pb-2 pt-1.5 text-left transition-colors",
        "data-[state=active]:shadow-none",
        active
          ? "border-b-[#08254c] bg-transparent"
          : "border-b-transparent hover:bg-[#f7f8fa]",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "flex h-6 w-6 flex-none items-center justify-center rounded-full border",
          active
            ? "border-[#ffc83d] bg-[#ffc83d] text-[#08254c]"
            : "border-[#d4dae3] text-[#7b8799]",
        ].join(" ")}
      >
        <LayoutDashboard className="h-3.5 w-3.5" />
      </span>
      <span className="flex flex-col leading-tight">
        <span
          className={[
            "text-[13px] font-semibold",
            active ? "text-[#08254c]" : "text-[#596b85]",
          ].join(" ")}
        >
          {t("Debrief summary")}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
          {t("for the executive")}
        </span>
      </span>
    </TabsTrigger>
  );
}
